/**
 * Base class for all components in the Entity Component System.
 * Enforces serialization contract: all components must be JSON-serializable.
 *
 * Components can be:
 * - Tag components (empty marker classes)
 * - Data components (simple properties)
 * - Behavior components (implement provider interfaces)
 *
 * The base constructor handles deserialization by assigning all properties
 * from the data object. Components with complex nested objects (like HexCoords)
 * should override the constructor to hydrate those objects.
 */
export abstract class Component {
  /**
   * Default constructor that handles both empty instantiation and deserialization.
   * Assigns all properties from the data object to this instance.
   *
   * Override this constructor for components that need:
   * - Validation of input data
   * - Hydration of nested objects (e.g., HexCoords.get())
   * - Default values for missing properties
   *
   * @param data - Optional object with properties to assign
   *
   * @example
   * ```typescript
   * // Simple component - just use base constructor
   * class Faction extends Component {
   *   name: string = '';
   * }
   *
   * // Complex component - override constructor
   * class HexPosition extends Component {
   *   coords: HexCoords;
   *   bearing: number;
   *
   *   constructor(data: { coords?: {q: number, r: number}, bearing?: number } = {}) {
   *     super();
   *     this.coords = HexCoords.get(data.coords ?? { q: 0, r: 0 });
   *     this.bearing = data.bearing ?? 1;
   *   }
   * }
   * ```
   */
  constructor(data: Record<string, any> = {}) {
    Object.assign(this, data);
  }

  /**
   * Default serialization: returns all own enumerable properties.
   * Override this method if you need custom serialization logic.
   *
   * @example
   * ```typescript
   * toJSON() {
   *   return {
   *     myProperty: this.myProperty,
   *     // Exclude computed properties or internal state
   *   };
   * }
   * ```
   */
  toJSON(): any {
    // Create a new object with all enumerable own properties
    return Object.assign({}, this);
  }

  /**
   * Equality comparison method.
   * Must be implemented by each concrete component class.
   *
   * Simple components using simpleComponent() wrapper get this auto-generated
   * with JSON comparison. Components with custom serialization must implement
   * their own equality logic (e.g., for nested objects like HexCoords).
   *
   * TypeScript cannot enforce instance methods be overridden,
   * so this throws by default to catch missing implementations at runtime.
   * The ComponentRegistry validates this at boot time.
   *
   * @param _other - Another instance of this component to compare against
   * @returns true if the components are equal, false otherwise
   *
   * @example
   * ```typescript
   * equals(other: this): boolean {
   *   return this.coords.equals(other.coords) &&
   *          this.bearing === other.bearing;
   * }
   * ```
   */
  equals(_other: this): boolean {
    throw new Error(
      `${this.constructor.name}.equals() not implemented. ` +
      `Use simpleComponent() wrapper or implement custom equals().`
    );
  }

  /**
   * Static method to provide test instances for automated testing.
   * Must be overridden by each concrete component class.
   *
   * TypeScript cannot enforce static methods on abstract classes,
   * so this throws by default to catch missing implementations at runtime.
   * The ComponentRegistry validates this at boot time.
   *
   * Simple components using simpleComponent() wrapper get this auto-generated.
   * Components with custom serialization should return multiple test instances
   * to cover edge cases.
   *
   * @returns Array of test instances for round-trip serialization testing
   *
   * @example
   * ```typescript
   * static getTestInstances(): MyComponent[] {
   *   return [
   *     new MyComponent(defaultValue),
   *     new MyComponent(edgeCase1),
   *     new MyComponent(edgeCase2),
   *   ];
   * }
   * ```
   */
  static getTestInstances(): Component[] {
    throw new Error(
      `${this.name}.getTestInstances() not implemented. ` +
      `Use simpleComponent() wrapper or implement custom getTestInstances().`
    );
  }
}

/**
 * Type constraint for component classes with serialization and testing.
 * Used by the registry to ensure type safety.
 */
export type ComponentClass<T extends Component = Component> = {
  new(data?: any): T;
  getTestInstances(): T[];
};
